import React, { useEffect, useRef } from 'react';
import { flow } from 'lodash-es';
/* Models */
import { InventoryEntities } from 'client/data/models/inventory';
/* Utils */
import { EventToolbox } from 'client/utils/event-toolbox';
import { getPrices } from 'site-modules/shared/utils/inventory-utils/get-prices';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { replaceNullChars } from 'site-modules/shared/utils/string';
import { stripHTML, transformNewlines } from 'site-modules/shared/utils/strip-html';
/* Constants */
import { ACTIONS } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { ADVERTISED_PRICE_SOURCE, DEALER_PRICE_DISCLAIMER } from 'site-modules/shared/constants/inventory/prices';
/* Components */
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { PricingBlock } from 'site-modules/shared/components/inventory/pricing-block/pricing-block';

import './price-details.scss';

export function PriceDetails({ vinData = {} }) {
  const wrapper = useRef();

  const { prices, type, computedDisplayInfo } = vinData;
  const { advertisedPrice, msrpPrice, advertisedPriceSource } = getPrices(prices, computedDisplayInfo, type);

  useEffect(() => {
    const data = { price: advertisedPrice };
    EventToolbox.fireCustomEvent(ACTIONS.PRICE_PROMISE_PRICE_DISPLAY, data, wrapper.current);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  const isNewVehicle = isNew(type);

  const msrpSubcopy = 'This is the suggested retail price of the vehicle, including manufacturer-equipped options.';
  const sanitizedDealerPriceDisclaimer =
    advertisedPrice && prices?.dealerPriceDisclaimer
      ? flow(replaceNullChars, stripHTML, transformNewlines)(prices.dealerPriceDisclaimer)
      : null;
  const dealerPriceDisclaimer = sanitizedDealerPriceDisclaimer ? (
    <ContentFragment>{`${DEALER_PRICE_DISCLAIMER}<br><br>Dealer note: ${sanitizedDealerPriceDisclaimer}`}</ContentFragment>
  ) : null;

  const dealerPrice = advertisedPriceSource === ADVERTISED_PRICE_SOURCE.INTERNET_PRICE ? advertisedPrice : null;

  return (
    <div className="price-details text-gray-darker" ref={wrapper} data-tracking-parent="edm-entry-price-analysis">
      <div className="price-details-block bg-white px-1 px-md-1_5 pb-1 pb-md-1_5 pt-1">
        {isNewVehicle && (
          <PricingBlock
            title={<h3 className="mb-0_5 fw-bold size-16">MSRP</h3>}
            price={msrpPrice}
            subcopy={msrpSubcopy}
            priceClasses="fw-bold"
            priceBlockClasses="py-1"
          />
        )}
        <PricingBlock
          title={<h3 className="mb-0_5 fw-bold size-16">Dealer Price</h3>}
          price={dealerPrice || 'Not available'}
          subcopy={dealerPriceDisclaimer || DEALER_PRICE_DISCLAIMER}
          priceClasses={dealerPrice ? 'fw-bold' : ''}
          priceBlockClasses="py-1"
        />
      </div>
    </div>
  );
}

PriceDetails.propTypes = {
  vinData: InventoryEntities.InventoryVin,
};
